import type { ApiEnvelope } from '@/api/errors';
import type {
  CreateSaleOfferRequest,
  ExchangeConstraints,
  ExchangeSellOrientation,
  ExchangeSession,
  PrepaymentType,
  SaleExchangeStatusInfo,
  SaleOffersByGoods,
  SaleOfferDraft,
  SaleOrder,
  SellTreeResponse,
  UpdateSaleOfferRequest,
  UploadedFile,
} from '@/types/sell';
import { BaseService } from './BaseService';

// Write-операции order-service на dev дольше дефолтных 15с (Camunda/нотификации) — даём 60с.
const WRITE_TIMEOUT_MS = 60_000;

export interface SaleOffersParams {
  readonly address: string;
  readonly addressTitle: string;
  readonly parentGoodsType: string;
  /** Год — только если сервер прислал его в узле дерева (выпуск А); без него — все лоты культуры (решение 2026-11-04). */
  readonly year?: number;
}

// Сервис order-service для раздела «Продам» (мои офферы продажи). Конверт { success, data }.
class SellService extends BaseService {
  /** Дерево моих офферов (базисы → товары с годом); GOODS_ITEM несёт updatedDate. */
  async getSaleTree(signal?: AbortSignal): Promise<SellTreeResponse> {
    const response = await this.request<ApiEnvelope<SellTreeResponse>>(
      { method: 'get', url: '/order/tree/sale' },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Офферы-лоты культуры на базисе (basis + товар + год) → массив лотов. */
  async getMySaleOffers(params: SaleOffersParams, signal?: AbortSignal): Promise<SaleOrder[]> {
    const response = await this.request<ApiEnvelope<{ goodsList?: SaleOffersByGoods[] }>>(
      { method: 'get', url: '/order/my/sale/tree/order_list', params },
      { idempotent: true, signal },
    );
    const goodsList = this.unwrap(response.data, response.status).goodsList ?? [];
    return goodsList.flatMap((item) => item.orders ?? []);
  }

  /** Все лоты базиса (по всем культурам/годам), сгруппированные по культуре — для списка «Продам». */
  async getMySaleOffersByBasis(
    params: { address: string; addressTitle: string },
    signal?: AbortSignal,
  ): Promise<SaleOffersByGoods[]> {
    const response = await this.request<ApiEnvelope<{ goodsList?: SaleOffersByGoods[] }>>(
      { method: 'get', url: '/order/my/sale/tree/order_list', params },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status).goodsList ?? [];
  }

  /** Полный оффер для просмотра/редактирования. */
  async getSaleOffer(orderId: number, signal?: AbortSignal): Promise<SaleOrder> {
    const response = await this.request<ApiEnvelope<SaleOrder>>(
      { method: 'get', url: '/order/sale', params: { orderId } },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Приостановить оффер (скрыть с рынка, можно возобновить). */
  async suspendOffer(orderId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/order/my/suspend', params: { orderId, orderType: 'SALE' } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Возобновить приостановленный оффер. */
  async unsuspendOffer(orderId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/order/my/unsuspend', params: { orderId, orderType: 'SALE' } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Удалить один оффер. */
  async deleteOffer(orderId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'delete', url: '/order/sale', params: { orderId } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Удалить все предложения культуры на базисе (за год — если он передан, см. SaleOffersParams.year). */
  async deleteByBasisGoods(params: SaleOffersParams): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      {
        method: 'delete',
        url: '/order/my/sale/orders',
        params: {
          parentGoodsType: params.parentGoodsType,
          address: params.address,
          addressTitle: params.addressTitle,
          year: params.year,
        },
      },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Биржа (НТБ-контур) ───────────────────────────────────────────────────────
  /**
   * Биржевые состояния моих офферов (id → {status, reason}) — для бейджа/действий. Возвращаются только
   * биржевые офферы с непустым состоянием; отсутствие id в карте = «не на бирже» (NOT_ON_EXCHANGE).
   */
  async getExchangeStatuses(
    ids: readonly number[],
    signal?: AbortSignal,
  ): Promise<Record<number, SaleExchangeStatusInfo>> {
    const response = await this.request<ApiEnvelope<Record<number, SaleExchangeStatusInfo>>>(
      { method: 'get', url: '/order/sale/exchange/statuses', params: { ids: ids.join(',') } },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status) ?? {};
  }

  /**
   * Ориентир цены биржевого оффера — лучшая нетто-FCA у ворот продавца + терминал. `null` — спроса на бирже
   * нет или выгодной цены не нашлось (эндпоинт вернул пустое тело data).
   */
  async getExchangeOrientation(orderId: number, signal?: AbortSignal): Promise<ExchangeSellOrientation | null> {
    const response = await this.request<ApiEnvelope<ExchangeSellOrientation | null>>(
      { method: 'get', url: `/order/sale/${orderId}/exchange/orientation` },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status) ?? null;
  }

  /** Ограничения биржевого предложения (допустимые культура/качество/объём) — чтобы форма их соблюдала. */
  async getExchangeConstraints(signal?: AbortSignal): Promise<ExchangeConstraints> {
    const response = await this.request<ApiEnvelope<ExchangeConstraints>>(
      { method: 'get', url: '/order/sale/exchange/constraints' },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Статус торговой сессии биржи (окно + признак «открыто сейчас») — для доступности публикации. */
  async getExchangeSession(signal?: AbortSignal): Promise<ExchangeSession> {
    const response = await this.request<ApiEnvelope<ExchangeSession>>(
      { method: 'get', url: '/order/sale/exchange/session' },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Опубликовать биржевой оффер на бирже (доступно только в окне торгов; иначе бэкенд отклонит). */
  async publishToExchange(orderId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/sale/${orderId}/exchange/publish` },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Снять биржевой оффер с биржи (после снятия оффер можно править/удалять/публиковать заново). */
  async removeFromExchange(orderId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/sale/${orderId}/exchange/remove` },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Справочники формы ───────────────────────────────────────────────────────
  /** Типы предоплаты для продажи (PERCENT_0/PERCENT_100 + проценты/названия). */
  async getPrepaymentTypes(signal?: AbortSignal): Promise<PrepaymentType[]> {
    const response = await this.request<ApiEnvelope<PrepaymentType[]>>(
      { method: 'get', url: '/order/prepayment_types', params: { orderType: 'SALE' } },
      { idempotent: true, signal },
    );
    return response.data?.data ?? [];
  }

  /** Способы отгрузки («Авто», «ЖД», «Элеватор», …). */
  async getShipmentModes(signal?: AbortSignal): Promise<string[]> {
    const response = await this.request<ApiEnvelope<string[]>>(
      { method: 'get', url: '/order/shipment_modes' },
      { idempotent: true, signal },
    );
    return response.data?.data ?? [];
  }

  // ── Файлы предложения (multipart) → дескрипторы ──────────────────────────────
  private async upload(url: string, files: File[]): Promise<UploadedFile[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));
    const response = await this.request<ApiEnvelope<{ files?: UploadedFile[] }>>(
      { method: 'post', url, data: formData },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    return this.unwrap(response.data, response.status).files ?? [];
  }

  uploadDeclaration(files: File[]): Promise<UploadedFile[]> {
    return this.upload('/order/sale/upload/declaration', files);
  }

  uploadTestReport(files: File[]): Promise<UploadedFile[]> {
    return this.upload('/order/sale/upload/testReport', files);
  }

  uploadSpecialTerms(files: File[]): Promise<UploadedFile[]> {
    return this.upload('/order/sale/upload/special_terms', files);
  }

  /** Удалить загруженный (ещё не привязанный) файл предложения. */
  async deleteSaleFile(fileId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'delete', url: '/order/sale/file', params: { fileId } },
      { retry: false },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Поручение агенту (синхронно: generate → sign) ────────────────────────────
  /** Сгенерировать черновик поручения агенту по данным предложения → дескриптор файла. */
  async generateAgentAssignment(draft: SaleOfferDraft): Promise<UploadedFile> {
    const response = await this.request<ApiEnvelope<UploadedFile>>(
      { method: 'post', url: '/order/sale/generate/agent_assignment', data: draft },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Подписать поручение агенту (серверная подпись, синхронно). */
  async signAgentAssignment(fileId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/sale/agent_assignment/${fileId}/sign` },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Создание / редактирование ────────────────────────────────────────────────
  /** Создать предложение → orderId (создание синхронно возвращает id). */
  async createOffer(request: CreateSaleOfferRequest): Promise<number> {
    const response = await this.request<ApiEnvelope<{ orderId?: number }>>(
      { method: 'post', url: '/order/sale/create', data: request },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    const orderId = this.unwrap(response.data, response.status).orderId;
    if (orderId == null) {
      throw new Error('Create sale offer response has no orderId');
    }
    return orderId;
  }

  /** Обновить предложение (оптимистичная блокировка по version). */
  async updateOffer(orderId: number, request: UpdateSaleOfferRequest): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/sale/update/${orderId}`, data: request },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }
}

export const sellService = new SellService();
